import React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import styles from './ButtonPublic.module.css';

export type ButtonPublicVariant = 'primary' | 'secondary' | 'sage' | 'burgundy';
export type ButtonPublicSize = 'sm' | 'md';

interface CommonProps {
  children: React.ReactNode;
  variant?: ButtonPublicVariant;
  size?: ButtonPublicSize;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  className?: string;
}

interface ButtonElementProps extends CommonProps {
  as?: 'button';
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  href?: never;
  target?: never;
  rel?: never;
}

interface AnchorElementProps extends CommonProps {
  as: 'a';
  href: string;
  target?: string;
  rel?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  disabled?: never;
}

export type ButtonPublicProps = ButtonElementProps | AnchorElementProps;

const isInternalRoute = (href: string): boolean =>
  href.startsWith('/') && !href.startsWith('//');

const renderContent = (
  icon: React.ReactNode,
  iconPosition: 'left' | 'right',
  children: React.ReactNode
): React.ReactNode => (
  <>
    {icon && iconPosition === 'left' && (
      <span className={styles.icon} aria-hidden="true">
        {icon}
      </span>
    )}
    <span className={styles.label}>{children}</span>
    {icon && iconPosition === 'right' && (
      <span className={styles.icon} aria-hidden="true">
        {icon}
      </span>
    )}
  </>
);

const ButtonPublic: React.FC<ButtonPublicProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'right',
  className,
  as = 'button',
  ...rest
}) => {
  const classes = classNames(
    styles.button,
    styles[variant],
    styles[size],
    className
  );
  const content = renderContent(icon, iconPosition, children);

  if (as === 'a') {
    const { href, target, rel, onClick } = rest as Omit<
      AnchorElementProps,
      keyof CommonProps | 'as'
    >;

    if (isInternalRoute(href)) {
      return (
        <Link to={href} className={classes} onClick={onClick}>
          {content}
        </Link>
      );
    }
    const computedRel =
      rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined);
    return (
      <a
        href={href}
        target={target}
        rel={computedRel}
        className={classes}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  const { onClick, disabled } = rest as Omit<
    ButtonElementProps,
    keyof CommonProps | 'as'
  >;
  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      disabled={disabled}
    >
      {content}
    </button>
  );
};

export { ButtonPublic };
export default ButtonPublic;
